/**
 * @file: DateSegments.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { DateRange } from '@euler/model/common';
import { dateRangeTillToday, today } from '@euler/utils/datetime';
import addMonths from 'date-fns/addMonths';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import startOfYear from 'date-fns/startOfYear';

export type DateRangeDef = {
  text: string;
  value: () => DateRange;
};

export type PredefinedDateRangeType =
  | 'recent7days'
  | 'recent15days'
  | 'recent30days'
  | 'recent60days'
  | 'recent90days'
  | 'recenthalfyear'
  | 'recent1year'
  | 'today'
  | 'thisweek'
  | 'thismonth'
  | 'lastmonth'
  | 'thisyear';

export const PredefinedDateRanges: {
  [key in PredefinedDateRangeType]: DateRangeDef;
} = {
  today: {
    text: '今日',
    value: () => ({ startDate: today(), startInclusive: true }),
  },
  recent7days: {
    text: '最近7天',
    value: () => dateRangeTillToday(7),
  },
  recent15days: {
    text: '最近15天',
    value: () => dateRangeTillToday(15),
  },
  recent30days: {
    text: '最近30天',
    value: () => dateRangeTillToday(30),
  },
  recent60days: {
    text: '最近60天',
    value: () => dateRangeTillToday(60),
  },
  recent90days: {
    text: '最近90天',
    value: () => dateRangeTillToday(90),
  },
  recenthalfyear: {
    text: '最近半年',
    value: () => dateRangeTillToday(6, 'month'),
  },
  recent1year: {
    text: '最近1年',
    value: () => dateRangeTillToday(1, 'year'),
  },
  thisweek: {
    text: '本周',
    value: () => ({
      startDate: startOfWeek(today(), { weekStartsOn: 1 }),
      startInclusive: true,
    }),
  },
  thismonth: {
    text: '本月',
    value: () => ({
      startDate: startOfMonth(today()),
      startInclusive: true,
    }),
  },
  lastmonth: {
    text: '上月',
    value: () => ({
      startDate: startOfMonth(addMonths(today(), -1)),
      startInclusive: true,
      endDate: startOfMonth(today()),
      endInclusive: false,
    }),
  },
  thisyear: {
    text: '今年',
    value: () => ({
      startDate: startOfYear(new Date()),
      startInclusive: true,
    }),
  },
};
